// src/pages/WorkerHelp.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  MessageCircle,
  Mail,
} from "lucide-react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE;

export default function WorkerHelp() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-blue-50 text-neutral-900">
      {/* =================== HEADER =================== */}
      <div className="text-center py-16">
        <h1 className="text-5xl font-extrabold bg-gradient-to-r from-blue-700 to-purple-600 bg-clip-text text-transparent mb-4">
          Worker Help & Support
        </h1>
        <p className="text-neutral-600 max-w-xl mx-auto text-lg">
          Stuck somewhere? Need help with verification, documents, or jobs?  
          We’re here for you — just reach out.
        </p>
      </div>

      {/* =================== FAQ SECTION =================== */}
      <div className="max-w-5xl mx-auto px-6">
        <h2 className="text-3xl font-bold text-center mb-10">
          Frequently Asked Questions
        </h2>
        <WorkerFAQ />
      </div>

      {/* =================== LIVE CHAT SECTION =================== */}
      <div className="max-w-5xl mx-auto mt-20 px-6">
        <div className="bg-gradient-to-br from-indigo-900 to-purple-800 rounded-3xl shadow-2xl p-10 text-white flex flex-col sm:flex-row justify-between items-center gap-8">
          <div className="text-center sm:text-left">
            <h2 className="text-2xl sm:text-3xl font-semibold mb-2">
              Need Live Support?
            </h2>
            <p className="text-indigo-200">
              Our worker support agents are available  
              <span className="font-semibold text-white"> Mon–Fri, 9AM – 5PM (PT)</span>
            </p>
          </div>

          <button
            onClick={() => navigate("/worker-live-chat")}
            className="flex items-center gap-2 px-8 py-3 bg-white text-indigo-900 font-semibold rounded-2xl hover:opacity-90 transition-all shadow-md hover:shadow-lg"
          >
            <MessageCircle className="w-5 h-5" />
            Start Live Chat
          </button>
        </div>
      </div>

      {/* =================== TICKET FORM =================== */}
      <WorkerTicketForm />

      {/* =================== FOOTER =================== */}
      <div className="border-t bg-neutral-50 py-6 text-center text-sm text-neutral-500 mt-10">
        © {new Date().getFullYear()} Book Your Need — Workers First.
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* 🎟 FULLY FIXED SUPPORT TICKET FORM — FETCHES DATA FROM MONGO              */
/* -------------------------------------------------------------------------- */
function WorkerTicketForm() {
  const [worker, setWorker] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const workerId = localStorage.getItem("workerId");

  // 🔥 Fetch worker profile from MongoDB
  useEffect(() => {
    async function fetchProfile() {
      try {
        const res = await axios.get(`${API_BASE}/api/worker/profile/${workerId}`);
        setWorker(res.data);
      } catch (err) {
        console.error("❌ Failed to fetch worker profile:", err);
      }
    }

    fetchProfile();
  }, [workerId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject || !message) return alert("Please fill out all fields.");
    if (!worker?.email) return alert("Cannot submit ticket — worker email missing.");

    setLoading(true);

    try {
      await axios.post(`${API_BASE}/api/support/send-worker-ticket`, {
        subject,
        message,
        workerEmail: worker.email,
        workerName: worker.name,
      });

      setSent(true);
      setSubject("");
      setMessage("");
    } catch (err) {
      console.error("❌ Ticket send failed:", err);
      alert("Failed to send ticket. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!worker) {
    return (
      <div className="text-center py-16">
        <p className="text-neutral-600">Loading your profile...</p>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="text-center py-20 px-6">
        <Mail className="w-12 h-12 text-green-600 mx-auto mb-3" />
        <h3 className="text-2xl font-semibold text-green-600 mb-2">
          Ticket Submitted Successfully
        </h3>
        <p className="text-neutral-600">
          Our worker support team will reach out to you shortly at{" "}
          <b>{worker.email}</b>.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto text-center py-24 px-6">
      <h2 className="text-3xl font-bold mb-4">Need Extra Help?</h2>
      <p className="text-neutral-600 mb-8 max-w-lg mx-auto">
        Send us a ticket and our team will get back to you within 24 hours.
      </p>

      <form
        onSubmit={handleSubmit}
        className="backdrop-blur-lg bg-white/60 border border-neutral-200 shadow-lg rounded-3xl p-8 transition hover:shadow-xl"
      >
        <input
          type="text"
          placeholder="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full mb-4 border border-neutral-300 rounded-xl p-3 focus:ring-2 focus:ring-indigo-700 focus:outline-none"
        />
        <textarea
          rows="4"
          placeholder="Describe your issue..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full mb-6 border border-neutral-300 rounded-xl p-3 focus:ring-2 focus:ring-indigo-700 focus:outline-none"
        />
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-indigo-900 text-white py-3 rounded-2xl text-lg font-medium hover:opacity-90 transition"
        >
          {loading ? "Sending..." : "Submit Ticket"}
        </button>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* 🧠 WORKER FAQ SECTION                                                      */
/* -------------------------------------------------------------------------- */
function WorkerFAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      q: "How long does my verification take?",
      a: "Document and background verification typically takes 24–48 hours. If anything needs correction, our team will notify you.",
    },
    {
      q: "Why was my ID or certification rejected?",
      a: "Most rejections happen due to unclear images or mismatched information. Simply re-upload the correct document.",
    },
    {
      q: "When do I get paid?",
      a: "Payments are sent automatically after the customer marks the job complete. Funds go directly to your connected payout method.",
    },
    {
      q: "How do I upload missing documents?",
      a: "Go to your Documents page and select the section that requires attention — everything is tracked clearly.",
    },
    {
      q: "Do I need a background check?",
      a: "Yes — all workers need ID verification. Additional checks may be required for certain job categories.",
    },
  ];

  return (
    <div className="grid sm:grid-cols-2 gap-6">
      {faqs.map((item, i) => (
        <div
          key={i}
          className={`relative rounded-3xl p-6 sm:p-8 transition-all duration-300 cursor-pointer border ${
            openIndex === i
              ? "backdrop-blur-xl bg-white/70 border-neutral-300 shadow-2xl"
              : "backdrop-blur-md bg-white/40 border-neutral-200 hover:bg-white/60 hover:shadow-lg"
          }`}
          onClick={() => setOpenIndex(openIndex === i ? null : i)}
        >
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg sm:text-xl font-semibold text-neutral-800 pr-4">
              {item.q}
            </h3>
            {openIndex === i ? (
              <ChevronUp className="w-5 h-5 text-neutral-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-neutral-500" />
            )}
          </div>

          <div
            className={`overflow-hidden transition-all duration-500 ${
              openIndex === i ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <p className="text-neutral-700 text-sm sm:text-base leading-relaxed">
              {item.a}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
